import React from "react";
import { Link } from "react-router-dom";
import "../styles/services.css";
import "../styles/integrations.css";
import "../styles/home.css";

import ContactSection from "./ContactSection";
import Footer from "./Footer";
import Seo from "./Seo";
import DataApparatusLogo from "../icons/logos/logoNew.png";
import {
  engagementModels,
  homepageOutcomeChoices,
  services,
} from "../data/siteContent";

export default function Home() {
  return (
    <div className="home">
      <Seo
        title="Data Apparatus | Operational Data Foundations for Automation, Reporting and Decisions"
        description="Data Apparatus helps businesses connect operational systems, automate data workflows and build trusted data foundations for reporting, compliance and decision-making."
      />

      <section className="home-hero clean" id="home">
        <div className="home-hero-inner clean">
          <div className="home-hero-copy">
            <span className="eyebrow">Operational data foundations</span>
            <h1>Connect, automate and govern the data behind your operations, reporting and decisions.</h1>
            <p>
              Data Apparatus helps businesses replace disconnected systems, manual workflows
              and inconsistent data with controlled data platforms, reliable integrations
              and executive-ready insight.
            </p>
            <div className="hero-actions">
              <Link to="/contact" className="primary-btn">
                Request a data platform review
              </Link>
              <a href="#ways-to-work" className="secondary-btn">
                See how we work
              </a>
            </div>
            <div className="hero-trust" aria-label="Key delivery domains">
              <span>Operational systems</span>
              <span>Financial services</span>
              <span>Sustainability data</span>
              <span>Workflow automation</span>
              <span>Semantic BI</span>
            </div>
          </div>

          <div className="hero-delivery-panel" aria-label="Data platform delivery model">
            <div className="delivery-panel-brand">
              <img src={DataApparatusLogo} alt="Data Apparatus" />
            </div>
            <div className="delivery-panel-steps">
              <div>
                <span>Review</span>
                <strong>Find system gaps, workflow bottlenecks and data risks</strong>
              </div>
              <div>
                <span>Build</span>
                <strong>Engineer integrations, pipelines, platforms and data models</strong>
              </div>
              <div>
                <span>Automate</span>
                <strong>Remove recurring spreadsheet, extract and workflow effort</strong>
              </div>
              <div>
                <span>Operate</span>
                <strong>Provide senior data capability and continuous improvement</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="buyer-journey-section" id="outcome-choice">
        <div className="buyer-journey-inner">
          <div className="buyer-journey-heading">
            <span className="eyebrow">Choose the outcome you need</span>
            <h2>Connect systems. Automate workflows. Govern the data behind better decisions.</h2>
            <p>
              Data Apparatus can review your operational data flows, build the data
              foundation, automate recurring workflows, or operate as your ongoing data
              partner.
            </p>
          </div>

          <div className="buyer-outcome-grid">
            {homepageOutcomeChoices.map((outcome) => (
              <article className="buyer-outcome-card" key={outcome.title}>
                <span>{outcome.number}</span>
                <h3>{outcome.title}</h3>
                <p>{outcome.copy}</p>
              </article>
            ))}
          </div>

          <div className="journey-model-strip" aria-label="Data Apparatus buyer journey">
            <strong>Review -&gt; Build -&gt; Automate -&gt; Operate</strong>
            <span>Delivered as: Fixed-Scope Review | Project Delivery | Monthly Data Partner</span>
          </div>
        </div>
      </section>

      <section className="work-models-section home-work-models" id="ways-to-work">
        <div className="work-models-inner">
          <div className="work-models-copy">
            <span className="eyebrow">Delivered in the way that fits your business</span>
            <h2>Fixed review, defined project or monthly data partner.</h2>
            <p>
              Each engagement is shaped around the level of support your business needs:
              from a fixed-scope review, to a defined implementation project, to monthly
              senior data capability.
            </p>
          </div>

          <div className="work-models-grid">
            {engagementModels.map((model) => (
              <article
                className={`work-model-card ${model.featured ? "featured" : ""}`}
                key={model.title}
              >
                <span className="work-model-number">{model.number}</span>
                <h3>{model.title}</h3>
                <p>{model.summary}</p>
                <ul>
                  {model.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <strong>{model.fit}</strong>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="services clean" id="services">
        <div className="services-split-inner">
          <div className="services-narrative">
            <span className="eyebrow">What we build</span>
            <h2>Services that connect operational systems into trusted data foundations.</h2>
            <p>
              We fix the foundation behind operations, reporting and compliance: system
              integration, ingestion, modelling, reconciliation, governance, automation and
              decision-ready data layers.
            </p>
            <div className="services-outcome-list" aria-label="Service outcomes">
              <span>Roadmap</span>
              <span>Integrations</span>
              <span>Data models</span>
              <span>Automation</span>
            </div>
          </div>

          <div className="services-panel-list">
            {services.map((item, index) => (
              <Link to={item.href} className="service-card service-panel" key={item.title}>
                <span className="service-index">{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </div>
                <strong>View service</strong>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="proof-preview-section" id="proof">
        <div className="section-heading">
          <span className="eyebrow">Blog and perspective</span>
          <h2>Practical articles, written around real lineage and platform problems.</h2>
          <p>
            The first blog article focuses on a living data lineage map: tracing Power BI
            metrics through Purview, ADF/Synapse, SQL logic, medallion layers and source
            systems.
          </p>
        </div>
        <div className="proof-preview-grid single">
          <Link to="/blog/living-data-lineage-map" className="service-card">
            <h3>Living data lineage map</h3>
            <p>
              A practical article and visual guide for tracing dashboard metrics back
              through Power BI, Purview, ADF/Synapse, SQL transformations, bronze tables
              and source systems.
            </p>
            <strong>Read the blog</strong>
          </Link>
        </div>
      </section>

      <section className="architecture-flow-section" id="architecture">
        <div className="section-heading">
          <span className="eyebrow">Operating architecture</span>
          <h2>A connectivity hub for operational data.</h2>
          <p>
            Source systems feed a governed data hub. The hub validates, reconciles and
            models data before activating it through workflows, semantic BI, compliance
            evidence and operational applications.
          </p>
        </div>

        <div className="architecture-hub-diagram" aria-label="Operational data hub architecture">
          <svg className="hub-connector-layer" viewBox="0 0 1180 560" aria-hidden="true">
            <g className="hub-line-shadow" fill="none" strokeLinecap="round">
              <path d="M300 110H428V238H520" />
              <path d="M300 202H444V255H520" />
              <path d="M300 294H444V272H520" />
              <path d="M300 386H428V289H520" />
              <path d="M660 238H752V110H880" />
              <path d="M660 255H736V202H880" />
              <path d="M660 272H736V294H880" />
              <path d="M660 289H752V386H880" />
            </g>
            <g className="hub-line-main" fill="none" strokeLinecap="round">
              <path d="M300 110H428V238H520" />
              <path d="M300 202H444V255H520" />
              <path d="M300 294H444V272H520" />
              <path d="M300 386H428V289H520" />
              <path d="M660 238H752V110H880" />
              <path d="M660 255H736V202H880" />
              <path d="M660 272H736V294H880" />
              <path d="M660 289H752V386H880" />
            </g>
          </svg>

          <div className="hub-node-list hub-sources" aria-label="Source systems">
            <div className="diagram-column-label">Inputs</div>
            <article className="architecture-node">
              <span>Customer & CRM</span>
              <strong>Salesforce · HubSpot · Dynamics</strong>
            </article>
            <article className="architecture-node">
              <span>Finance & ERP</span>
              <strong>Xero · SAP · NetSuite · ledgers</strong>
            </article>
            <article className="architecture-node">
              <span>Operations</span>
              <strong>Assets · meters · work orders</strong>
            </article>
            <article className="architecture-node">
              <span>Files & APIs</span>
              <strong>Excel · SharePoint · SFTP · REST</strong>
            </article>
          </div>

          <article className="architecture-hub">
            <h3>Trusted Data Foundation</h3>
            <p>One controlled layer for operational data movement, quality and activation.</p>
            <div className="hub-stage-list" aria-label="Hub processing stages">
              <b>Ingest</b>
              <b>Validate</b>
              <b>Reconcile</b>
              <b>Model</b>
              <b>Govern</b>
            </div>
            <div className="hub-platform-list">
              <i>Microsoft Fabric</i>
              <i>Azure SQL</i>
              <i>Snowflake</i>
              <i>Databricks</i>
            </div>
          </article>

          <div className="hub-node-list hub-outputs" aria-label="Activation outputs">
            <div className="diagram-column-label">Activation</div>
            <article className="architecture-node">
              <span>Workflow automation</span>
              <strong>Approvals · write-back · exceptions</strong>
            </article>
            <article className="architecture-node">
              <span>Semantic BI</span>
              <strong>Metrics · models · dashboards</strong>
            </article>
            <article className="architecture-node">
              <span>Compliance evidence</span>
              <strong>Lineage · audit packs · evidence</strong>
            </article>
            <article className="architecture-node">
              <span>Operational apps</span>
              <strong>Customer · finance · asset workflows</strong>
            </article>
          </div>

          <div className="architecture-control-band">
            <span>Monitoring</span>
            <span>Data quality</span>
            <span>Lineage</span>
            <span>Ownership</span>
          </div>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
}
